import { z } from 'zod';

import { TravelCancellationSchema } from './travel-cancellation.js';

/**
 * Standalone flight booking: everything after FareQuote.
 *
 * Search / FareUpsell / FareRule / FareQuote live in `tbo-flight.ts` — they are
 * read-only price discovery and were already shipped. This file covers the
 * mutating half of the funnel described in
 * `docs/architecture/TBO-STANDALONE-BOOKING-PLAN.md` §5.1:
 *
 *   FareQuote → session → pax → SSR → pay → Book/Ticket → GetBookingDetails
 *
 * Two invariants shape these types:
 *
 * 1. **The client never sends `TraceId` / `ResultIndex` after the session is
 *    created.** Both live server-side on the booking row. A client that could
 *    name a ResultIndex could book a fare it was never quoted. Every route below
 *    keys off `sessionId` only.
 * 2. **Money is a numeric string of minor units (paise)**, matching the rest of
 *    the platform. JSON has no BigInt; a `number` silently loses precision past
 *    2^53, which is reachable for group bookings in minor units.
 */

// ---- Passengers ---------------------------------------------------------

export const FlightPaxTypeSchema = z.enum(['ADULT', 'CHILD', 'INFANT']);
export type FlightPaxType = z.infer<typeof FlightPaxTypeSchema>;

export const FlightPaxTitleSchema = z.enum(['Mr', 'Mrs', 'Ms', 'Miss', 'Mstr', 'Dr']);
export type FlightPaxTitle = z.infer<typeof FlightPaxTitleSchema>;

/**
 * What the traveller tells us, which is not the same as what the ticket can
 * carry.
 *
 * `OTHER` is stored on the booking and shown back throughout our own screens.
 * TBO's Book/Ticket contract has no value for it — `Gender` there is 1 or 2 and
 * nothing else — so `flight-booking-payload.ts` derives what the airline is
 * sent. Keeping the fuller value here rather than flattening it on the way in
 * means the booking record stays true even though the ticket cannot be.
 *
 * Matches the `OTHER` that already exists on Prisma's shared `Gender` enum, so
 * this needed no migration.
 */
export const FlightPaxGenderSchema = z.enum(['MALE', 'FEMALE', 'OTHER']);
export type FlightPaxGender = z.infer<typeof FlightPaxGenderSchema>;

/**
 * Gender as the title states it. The traveller form has no Gender field - two
 * pickers for one fact let "Ms" + "Male" onto a PNR - so the API derives it
 * here on save, and the TBO payload reads it the same way.
 *
 * Null for a title that does not say (`Dr`, only on older bookings).
 */
export function flightPaxGenderForTitle(title: string | null | undefined): FlightPaxGender | null {
  switch ((title ?? '').trim()) {
    case 'Mrs':
    case 'Ms':
    case 'Miss':
      return 'FEMALE';
    case 'Mr':
    case 'Mstr':
      return 'MALE';
    default:
      return null;
  }
}

/**
 * Airline name rules, not general-purpose name rules: the ticket must match the
 * passport machine-readable zone, which is A–Z plus space/hyphen/apostrophe.
 * TBO rejects anything else at Book — i.e. *after* the money is taken — so this
 * is enforced at the schema boundary rather than left to the supplier.
 */
const paxNameSchema = z
  .string()
  .trim()
  .min(2, 'Must be at least 2 characters')
  .max(60)
  .regex(/^[A-Za-z][A-Za-z\s'-]*$/, 'Use letters, spaces, hyphens and apostrophes only');

export const FlightPassengerInputSchema = z.object({
  paxType: FlightPaxTypeSchema,
  title: FlightPaxTitleSchema,
  /** No middle-name field by design — TBO has no slot for it; append it here. */
  firstName: paxNameSchema,
  lastName: paxNameSchema,
  /** Optional and ignored when the title states it - see `flightPaxGenderForTitle`. */
  gender: FlightPaxGenderSchema.optional(),
  /** YYYY-MM-DD. Mandatory for CHILD/INFANT; many carriers want it for adults. */
  dateOfBirth: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD')
    .nullable()
    .default(null),
  /** ISO-3166-1 alpha-2. Mandatory on TBO's Book for every passenger. */
  nationality: z.string().trim().length(2).nullable().default(null),
  passportNumber: z.string().trim().min(5).max(20).nullable().default(null),
  /**
   * YYYY-MM-DD. Must clear the return date by 6 months on most sectors.
   *
   * Rides with `passportNumber`, never without it: TBO has no tier that takes
   * the number alone.
   */
  passportExpiry: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD')
    .nullable()
    .default(null),
  /**
   * YYYY-MM-DD. Part of the "full detail" tier alongside the issuing country —
   * `IsPassportFullDetailRequiredAtBook`, sent as `PassportIssueDate`.
   */
  passportIssueDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD')
    .nullable()
    .default(null),
  /** Sent to TBO as `PassportIssueCountryCode`. */
  passportIssuingCountry: z.string().trim().length(2).nullable().default(null),
  /** Indian tax ID. FareQuote's `IsPanRequiredAtBook` decides if it is needed. */
  panNumber: z
    .string()
    .trim()
    .regex(/^[A-Z]{5}[0-9]{4}[A-Z]$/, 'Invalid PAN')
    .nullable()
    .default(null),
  frequentFlyerNumber: z.string().trim().max(30).nullable().default(null),
  frequentFlyerAirline: z.string().trim().length(2).nullable().default(null),
});
export type FlightPassengerInput = z.infer<typeof FlightPassengerInputSchema>;

/** Contact block — captured once per booking, not per passenger. */
export const FlightContactInputSchema = z.object({
  email: z.string().trim().email().max(120),
  /** Country dialling code without the `+`, e.g. "91". */
  phoneCountryCode: z
    .string()
    .trim()
    .regex(/^\d{1,4}$/, 'Digits only'),
  phone: z
    .string()
    .trim()
    .regex(/^\d{6,14}$/, 'Digits only'),
  addressLine: z.string().trim().min(5, 'Address is required').max(200),
  city: z.string().trim().min(2, 'City is required').max(80),
  /** ISO-3166-1 alpha-2. */
  countryCode: z.string().trim().length(2).default('IN'),
});
export type FlightContactInput = z.infer<typeof FlightContactInputSchema>;

/** Optional GST block for Indian business bookings (`IsGSTMandatory` on quote). */
export const FlightGstInputSchema = z.object({
  gstNumber: z
    .string()
    .trim()
    .regex(/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][0-9A-Z]{3}$/, 'Invalid GSTIN'),
  companyName: z.string().trim().min(2).max(120),
  companyAddress: z.string().trim().max(200).nullable().default(null),
  companyEmail: z.string().trim().email().max(120).nullable().default(null),
  companyPhone: z.string().trim().max(20).nullable().default(null),
});
export type FlightGstInput = z.infer<typeof FlightGstInputSchema>;

// ---- Session creation ---------------------------------------------------

/**
 * One priced leg handed to session creation. A split domestic round trip is two
 * independent TBO sessions, so each leg carries its own TraceId + ResultIndex —
 * this is the last point at which the client supplies them.
 */
export const FlightBookingLegInputSchema = z.object({
  traceId: z.string().trim().min(1),
  /** The ResultIndex the traveller actually picked — post-upsell if they upsold. */
  resultIndex: z.string().trim().min(1),
  /** Chosen branded fare family, for display and reconciliation. Null if none. */
  fareFamilyName: z.string().trim().max(80).nullable().default(null),
  /**
   * SSR codes the chosen fare family already includes (`IsIncluded = true`).
   * These are re-priced to zero when the SSR catalogue is rendered. Charging for
   * a seat the bundle covers is the classic TBO upsell bug — it raises no API
   * error, so the guard has to be carried explicitly.
   */
  includedSsrCodes: z.array(z.string().trim().min(1)).max(50).default([]),
});
export type FlightBookingLegInput = z.infer<typeof FlightBookingLegInputSchema>;

export const CreateFlightBookingSessionInputSchema = z.object({
  legs: z.array(FlightBookingLegInputSchema).min(1).max(6),
  adults: z.number().int().min(1).max(9),
  children: z.number().int().min(0).max(8).default(0),
  infants: z.number().int().min(0).max(9).default(0),
  currencyCode: z.string().trim().length(3).default('INR'),
});
export type CreateFlightBookingSessionInput = z.infer<typeof CreateFlightBookingSessionInputSchema>;

// ---- Pricing breakdown --------------------------------------------------

/**
 * Every component persisted so an invoice can be rebuilt years later without
 * re-running the calculator (and without the FX rate of the day it is opened).
 */
export const FlightFareBreakdownSchema = z.object({
  /** Supplier net, converted to the sell currency. */
  basePaise: z.string().regex(/^-?\d+$/),
  taxPaise: z.string().regex(/^-?\d+$/),
  /** Chargeable seats / meals / baggage, after zeroing bundle-included SSRs. */
  ssrPaise: z.string().regex(/^-?\d+$/),
  markupPaise: z.string().regex(/^-?\d+$/),
  convenienceFeePaise: z.string().regex(/^-?\d+$/),
  gstPaise: z.string().regex(/^-?\d+$/),
  /**
   * Rounding to the whole unit, signed, so the rows still sum to `totalPaise`.
   * Derived from the stored booking; absent (older API) reads as 0.
   */
  roundOffPaise: z
    .string()
    .regex(/^-?\d+$/)
    .optional(),
  /** What the traveller is charged. The only figure sent to the gateway. */
  totalPaise: z.string().regex(/^-?\d+$/),
  currencyCode: z.string(),
});
export type FlightFareBreakdown = z.infer<typeof FlightFareBreakdownSchema>;

// ---- SSR (seats / meals / baggage) --------------------------------------

export const FlightSsrKindSchema = z.enum(['SEAT', 'MEAL', 'BAGGAGE']);
export type FlightSsrKind = z.infer<typeof FlightSsrKindSchema>;

/**
 * One purchasable ancillary, normalised across the LCC and non-LCC SSR shapes
 * (TBO returns quite different payloads for each).
 */
export const FlightSsrItemSchema = z.object({
  kind: FlightSsrKindSchema,
  /** TBO's own code (`SSRCode` / `Code`), echoed back verbatim at Book/Ticket. */
  code: z.string(),
  description: z.string(),
  pricePaise: z.string().regex(/^-?\d+$/),
  currencyCode: z.string(),
  /**
   * True when the chosen fare family already covers this code. Rendered as
   * "included" and excluded from the payable total.
   */
  isIncludedInFare: z.boolean(),
  /** Which leg (index into the session's legs) this belongs to. */
  legIndex: z.number().int().min(0),
  /** Flight segment within the leg — seats and meals are per-segment. */
  segmentIndex: z.number().int().min(0),
  /** SEAT only: cabin row/column, e.g. "12A". Null for meals and baggage. */
  seatNumber: z.string().nullable(),
  /** SEAT only: whether the map marks it occupied. */
  isBooked: z.boolean(),
  /** Free-form supplier extras needed to echo the row back (weight, craft, …). */
  raw: z.record(z.string(), z.unknown()).nullable(),
});
export type FlightSsrItem = z.infer<typeof FlightSsrItemSchema>;

export const FlightSsrCatalogueSchema = z.object({
  sessionId: z.string(),
  seats: z.array(FlightSsrItemSchema),
  meals: z.array(FlightSsrItemSchema),
  baggage: z.array(FlightSsrItemSchema),
  currencyCode: z.string(),
});
export type FlightSsrCatalogue = z.infer<typeof FlightSsrCatalogueSchema>;

/** One traveller's pick. `paxIndex` addresses `passengers[]` on the session. */
export const FlightSsrSelectionSchema = z.object({
  paxIndex: z.number().int().min(0),
  kind: FlightSsrKindSchema,
  code: z.string().trim().min(1),
  legIndex: z.number().int().min(0),
  segmentIndex: z.number().int().min(0),
});
export type FlightSsrSelection = z.infer<typeof FlightSsrSelectionSchema>;

export const SaveFlightSsrInputSchema = z.object({
  selections: z.array(FlightSsrSelectionSchema).max(200).default([]),
});
export type SaveFlightSsrInput = z.infer<typeof SaveFlightSsrInputSchema>;

// ---- Passenger save -----------------------------------------------------

export const SaveFlightPassengersInputSchema = z.object({
  passengers: z.array(FlightPassengerInputSchema).min(1).max(9),
  contact: FlightContactInputSchema,
  gst: FlightGstInputSchema.nullable().default(null),
});
export type SaveFlightPassengersInput = z.infer<typeof SaveFlightPassengersInputSchema>;

// ---- Required-field flags ----------------------------------------------

/**
 * FareQuote tells us which pax fields the *carrier* demands. The form is driven
 * by these rather than by a hardcoded "is it international?" guess — a missing
 * passport surfaces at Book, which is after the charge.
 */
export const FlightPaxRequirementsSchema = z.object({
  passportRequired: z.boolean(),
  /** Expiry + issuing country on top of the number. */
  passportFullDetailRequired: z.boolean(),
  panRequired: z.boolean(),
  gstMandatory: z.boolean(),
  /** DOB is compulsory for every pax type on this fare, not just child/infant. */
  dobRequiredForAdults: z.boolean(),
  /**
   * The fare will not ticket until a seat / a meal is chosen for every
   * passenger on every leg — TBO's `isseatmandatory` / `ismealmandatory`, set on
   * special fares like Super 6E and SpiceMax.
   *
   * These are not document fields, so nothing on the traveller form can satisfy
   * them; what they gate is a *step*. Read them or a booking sails past the
   * seat map, pays, and fails at ticketing with "Meal selection is mandatory" —
   * which is a real failure in this system's own supplier log.
   *
   * Defaulted so a `paxRequirements` snapshot stored before these existed still
   * parses as "not demanded", which is what those older fares said anyway.
   */
  seatMandatory: z.boolean().default(false),
  mealMandatory: z.boolean().default(false),
});
export type FlightPaxRequirements = z.infer<typeof FlightPaxRequirementsSchema>;

/**
 * The same flags resolved against the route, per field.
 *
 * `requirements` above is what the carrier asked for; this is what the customer
 * is actually made to fill in, and it is computed once on the server so the
 * form's "(Optional)" label and the validator that rejects the save can never
 * answer differently. See `flightDocumentRules`.
 */
export const FlightDocumentRulesSchema = z.object({
  nationality: z.boolean(),
  passportNumber: z.boolean(),
  passportExpiry: z.boolean(),
  passportIssueDate: z.boolean(),
  passportIssuingCountry: z.boolean(),
  /**
   * DOB from adults too (child and infant always give one). Defaulted to TRUE
   * — the stricter reading — so a session view from an API that predates the
   * field keeps asking every traveller, exactly as that API's validator still
   * does, rather than hiding a field the save would then be rejected for.
   */
  adultDateOfBirth: z.boolean().default(true),
});
export type FlightDocumentRulesView = z.infer<typeof FlightDocumentRulesSchema>;

// ---- Session view -------------------------------------------------------

export const FlightBookingStatusSchema = z.enum([
  'DRAFT',
  'PRICED',
  'PAX_ENTERED',
  'PAYMENT_PENDING',
  'PAID',
  'BOOKING',
  /**
   * Paid, and parked: the supplier refused for a reason that is ours to fix
   * (an agency wallet, a credential), so the money is kept and an operator
   * either re-issues or releases the hold. The name is deliberately neutral -
   * this value reaches the traveller's status page, which shows "processing"
   * and offers a cancel, and never the reason.
   */
  'ON_HOLD',
  'CONFIRMED',
  'BOOK_FAILED',
  /**
   * Raised with TBO, no terminal answer yet. Distinct from `CANCELLED` because
   * a change request can still come back `Rejected`, and a booking marked
   * cancelled on the strength of the request being *accepted* is a traveller
   * told their ticket is gone while they are still ticketed.
   */
  'CANCELLATION_REQUESTED',
  'CANCELLED',
  'REFUNDED',
  'EXPIRED',
  /**
   * A non-LCC fare the traveller LOCKED: a deposit was paid, the airline holds
   * the seat under a real PNR, and the balance is owed before the ticketing
   * deadline (`holdExpiresAt`). Unlike `ON_HOLD` - a PAID booking parked on our
   * supplier-wallet fault - here the customer deliberately paid only a part, no
   * ticket exists yet, and the hold lapses (seat released, deposit forfeited) if
   * the balance never lands.
   */
  'HELD',
]);
export type FlightBookingStatus = z.infer<typeof FlightBookingStatusSchema>;

/** Per-passenger ticket detail on a confirmed booking. */
export const FlightTicketedPaxSchema = z.object({
  firstName: z.string(),
  lastName: z.string(),
  paxType: FlightPaxTypeSchema,
  ticketNumber: z.string().nullable(),
  seat: z.string().nullable(),
  meal: z.string().nullable(),
  /**
   * The airline's PDF417 boarding-pass string for this traveller, verbatim from
   * the supplier (TBO `BarcodeDetails.Barcode[].Content`) - the IATA BCBP the
   * ticket renders as a scannable stripe. Null when the supplier did not return
   * one; defaulted so a session cached before this field existed still parses.
   */
  boardingPassBarcode: z.string().nullable().default(null),
});
export type FlightTicketedPax = z.infer<typeof FlightTicketedPaxSchema>;

export const FlightBookingSessionSchema = z.object({
  sessionId: z.string(),
  reference: z.string(),
  status: FlightBookingStatusSchema,
  /** Whole-journey itinerary snapshot, one entry per leg. */
  legs: z.array(
    z.object({
      fareFamilyName: z.string().nullable(),
      isLcc: z.boolean(),
      isRefundable: z.boolean(),
      /** Loosely typed: this is a verbatim supplier snapshot for display. */
      segments: z.array(z.record(z.string(), z.unknown())),
    }),
  ),
  adults: z.number().int(),
  children: z.number().int(),
  infants: z.number().int(),
  fare: FlightFareBreakdownSchema,
  /** The GST block the traveller entered, echoed back for the booking page + e-ticket. */
  gst: FlightGstInputSchema.nullable().default(null),
  requirements: FlightPaxRequirementsSchema,
  /**
   * Per-field document rules, already resolved against the route. The traveller
   * form labels and the client-side check read this, never `requirements`.
   *
   * Defaulted so a session cached from before this field existed still parses;
   * the server always sends it.
   */
  /**
   * The journey crosses a border. Advisory ONLY — it decides nothing about what
   * is mandatory (`documents` does that, off the carrier's flags). It exists so
   * the form can tell a traveller that a passport will be wanted at the airport
   * on a fare whose airline did not ask us for one.
   */
  internationalSector: z.boolean().default(false),
  documents: FlightDocumentRulesSchema.default({
    nationality: true,
    passportNumber: false,
    passportExpiry: false,
    passportIssueDate: false,
    passportIssuingCountry: false,
    adultDateOfBirth: true,
  }),
  /** ISO timestamp the price lock lapses at. Drives the countdown. */
  priceExpiresAt: z.string().nullable(),
  /**
   * What the traveller has already saved on this session, echoed back in the
   * same shape the save accepts.
   *
   * The browser keeps its working copy of the form in per-tab storage, so a
   * session reopened in a fresh tab - from "Continue your booking", a reminder
   * link, another device - arrives with nothing local. These let the form
   * pick up from the server's copy instead of asking for every name again.
   * Empty / null until the passenger step has been saved once. Defaulted so a
   * session cached before the fields existed still parses.
   */
  savedPassengers: z.array(FlightPassengerInputSchema).default([]),
  savedContact: FlightContactInputSchema.nullable().default(null),
  /** The ancillaries saved so far, in the shape the SSR save accepts. */
  savedSsr: z.array(FlightSsrSelectionSchema).default([]),
  /**
   * While `ON_HOLD`: when the hold is released automatically if nobody acts.
   * The status page turns it into "usually within N hours" and offers a
   * cancel. Null in every other status. Defaulted so a cached session from
   * before the field existed still parses.
   */
  holdExpiresAt: z.string().nullable().default(null),
  /**
   * Deposit already collected on a price-lock hold, in paise. `'0'` on any
   * booking that was never locked. On a `HELD` booking this is what the traveller
   * has paid; the balance still owed is `balanceDuePaise`. Defaulted so a session
   * cached before the field existed still parses.
   */
  holdDepositPaise: z
    .string()
    .regex(/^-?\d+$/)
    .default('0'),
  /**
   * Balance still owed on a `HELD` booking, in paise (`fare.totalPaise -
   * holdDepositPaise`). Null unless the booking is `HELD`. This is the amount the
   * "Pay balance" order is minted for.
   */
  balanceDuePaise: z
    .string()
    .regex(/^-?\d+$/)
    .nullable()
    .default(null),
  /**
   * A gateway order has been minted for this booking.
   *
   * The confirmation page needs to tell two `PAYMENT_PENDING` bookings apart:
   * one nobody ever tried to pay for, and one where the checkout was opened and
   * the capture simply has not been reported back yet. Only the first may be
   * offered a "Back to payment" button — showing it on the second puts a
   * pay-again control in front of someone whose money has already left their
   * account.
   *
   * The page used to answer that question from a browser-local stash written at
   * checkout, which is correct on the device that paid and wrong everywhere
   * else: the same link opened on a laptop, or after the tab's storage was
   * cleared, reported "Payment not completed" on a booking that was in fact
   * paid. This flag is on the booking itself, so every device gets the same
   * answer.
   *
   * Deliberately a boolean, not the order id: the client has no legitimate use
   * for a gateway identifier, and publishing one on a bearer-capability URL
   * would hand anyone with the link something to quote at support.
   */
  paymentInitiated: z.boolean().default(false),
  /** Set once Book/Ticket lands. */
  pnr: z.string().nullable(),
  ticketedPassengers: z.array(FlightTicketedPaxSchema),
  failureReason: z.string().nullable(),
  /**
   * Where cancellation stands. Always present — `NOT_APPLICABLE` on a booking
   * that never confirmed — so the page never has to tell "no data" apart from
   * "cannot cancel".
   */
  cancellation: TravelCancellationSchema,
});
export type FlightBookingSession = z.infer<typeof FlightBookingSessionSchema>;

// ---- Payment ------------------------------------------------------------

/**
 * Mirrors `CheckoutInitResult` from the payments module — the same Razorpay
 * Order handshake the itinerary share page uses, pointed at a flight booking.
 * Reusing the gateway means no second PCI surface and no second webhook path.
 */
export const FlightPaymentInitSchema = z.object({
  provider: z.enum(['PAYU', 'RAZORPAY']),
  keyId: z.string(),
  orderId: z.string(),
  amountPaise: z.string().regex(/^-?\d+$/),
  currencyCode: z.string(),
  description: z.string(),
  prefill: z.object({
    name: z.string(),
    email: z.string(),
    contact: z.string(),
  }),
});
export type FlightPaymentInit = z.infer<typeof FlightPaymentInitSchema>;

export const VerifyFlightPaymentInputSchema = z.object({
  orderId: z.string().trim().min(1),
  paymentId: z.string().trim().min(1),
  signature: z.string().trim().min(1),
});
export type VerifyFlightPaymentInput = z.infer<typeof VerifyFlightPaymentInputSchema>;

export const VerifyFlightPaymentResultSchema = z.object({
  acknowledged: z.literal(true),
  /** Replay of an already-settled payload — not an error, just a no-op. */
  alreadyPaid: z.boolean(),
  status: FlightBookingStatusSchema,
});
export type VerifyFlightPaymentResult = z.infer<typeof VerifyFlightPaymentResultSchema>;

// ---- Cancellation -------------------------------------------------------
//
// The flight-specific quote and result schemas that used to live here were
// replaced by the product-neutral ones in `travel-cancellation.ts`. Both funnels
// present the same four things — a quote, a reason, a pending request, an answer
// — and the pair of near-identical schemas had already started to drift.
